// In-browser canvas filters that turn an uploaded pet photo into a
// drawing impression for the visualiser:
//   - line drawing: clean ink outlines on a transparent ground
//   - coloured + filled: posterised flat colour with a drawn outline
//
// Phase 86: the line layer is now a Difference-of-Gaussians (DoG)
// band-pass with a smoothstep ramp, not raw Sobel-threshold. DoG is the
// classic basis for pencil / line-art (it's brightness-unbiased and
// suppresses flat-area noise), so the preview reads as a deliberate
// drawing instead of a noisy edge map. Still 100% client-side, no API,
// no per-use cost. The knobs below are tuned for pet/portrait photos
// and are safe to nudge.
//
// Preview aid only. Our artists hand-draw the real artwork from the
// customer's photo; this just shows them roughly what to expect.

// DoG line-art tuning (all on a 0..255 luminance scale):
const DOG_SIGMA1 = 1.4; // inner Gaussian (fine detail kept)
const DOG_SIGMA2 = 2.24; // outer Gaussian (~1.6x = canonical DoG band)
const INK_FLOOR = 1.6; // drop DoG response below this (kills fur noise)
const INK_SPAN = 11; // ramp width above the floor (smaller = harder lines)
const INK_GAMMA = 0.85; // <1 thickens, >1 thins the lines

const clampIdx = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

// Separable box blur of a given radius, edge-clamped, O(n) per pass via a
// sliding window sum. Three of these approximate a Gaussian.
function boxBlurRadius(
  src: Float32Array,
  w: number,
  h: number,
  r: number,
): Float32Array {
  if (r < 1) return src.slice();
  const norm = 1 / (2 * r + 1);
  const tmp = new Float32Array(src.length);
  const out = new Float32Array(src.length);
  // Horizontal pass.
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sum = 0;
    for (let k = -r; k <= r; k++) sum += src[row + clampIdx(k, 0, w - 1)];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = sum * norm;
      sum +=
        src[row + clampIdx(x + r + 1, 0, w - 1)] -
        src[row + clampIdx(x - r, 0, w - 1)];
    }
  }
  // Vertical pass.
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let k = -r; k <= r; k++) sum += tmp[clampIdx(k, 0, h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = sum * norm;
      sum +=
        tmp[clampIdx(y + r + 1, 0, h - 1) * w + x] -
        tmp[clampIdx(y - r, 0, h - 1) * w + x];
    }
  }
  return out;
}

// 3-pass box blur ~= Gaussian of the given sigma.
function gaussApprox(
  src: Float32Array,
  w: number,
  h: number,
  sigma: number,
): Float32Array {
  const r = Math.max(1, Math.round(sigma));
  return boxBlurRadius(boxBlurRadius(boxBlurRadius(src, w, h, r), w, h, r), w, h, r);
}

// DoG ink strength per pixel, in [0,1]. High where there's a dark-side
// intensity edge (an ink line), 0 in flat areas. Brightness-unbiased
// because pure DoG (g1 - g2) is zero-mean across flat regions.
function dogInk(gray: Float32Array, w: number, h: number): Float32Array {
  const g1 = gaussApprox(gray, w, h, DOG_SIGMA1);
  const g2 = gaussApprox(gray, w, h, DOG_SIGMA2);
  const n = w * h;
  const ink = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    // Dark side of an edge => g1 < g2 => (g2 - g1) positive.
    let s = (g2[i] - g1[i] - INK_FLOOR) / INK_SPAN;
    s = s < 0 ? 0 : s > 1 ? 1 : s;
    ink[i] = s === 0 || s === 1 ? s : Math.pow(s, INK_GAMMA);
  }
  return ink;
}

// Bounding box of pixels with alpha above `minA`. Null when nothing is
// opaque enough to matter.
function opaqueBounds(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  minA: number,
): { x: number; y: number; w: number; h: number } | null {
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > minA) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

// Parse a #rrggbb hex into an [r,g,b] triple (falls back to graphite).
function hexToRgb(hex: string | undefined): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? "").trim());
  if (!m) return [32, 28, 34];
  const v = parseInt(m[1], 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

/**
 * Render `img` as a drawing-style PNG blob. `coloured` picks the
 * coloured + filled look; otherwise a line drawing, with the lines
 * stitched in `lineHex` (the chosen thread colour). Returns null if the
 * image can't be read (e.g. a tainted canvas).
 */
export async function renderDrawing(
  img: HTMLImageElement,
  coloured: boolean,
  lineHex?: string,
): Promise<Blob | null> {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return null;

  // Work at a capped resolution so the per-pixel passes stay fast.
  const maxSide = 760;
  const scale = Math.min(1, maxSide / Math.max(iw, ih));
  const w = Math.max(1, Math.round(iw * scale));
  const h = Math.max(1, Math.round(ih * scale));

  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);

  let src: ImageData;
  try {
    src = ctx.getImageData(0, 0, w, h);
  } catch {
    // Tainted canvas (cross-origin without CORS) — bail gracefully.
    return null;
  }
  const d = src.data;
  const n = w * h;

  // Greyscale luminance — the DoG does its own blurring internally.
  const gray = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    gray[i] =
      0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
  }

  // Phase 86: clean line-art ink strength via Difference-of-Gaussians.
  const ink = dogInk(gray, w, h);

  const out = ctx.createImageData(w, h);
  const o = out.data;

  if (!coloured) {
    // Line drawing — ink lines in the chosen thread colour, rest clear.
    const [lr, lg, lb] = hexToRgb(lineHex);
    for (let i = 0; i < n; i++) {
      let a = Math.round(ink[i] * 255);
      if (d[i * 4 + 3] < 24) a = 0; // honour transparent (bg-removed) areas
      o[i * 4] = lr;
      o[i * 4 + 1] = lg;
      o[i * 4 + 2] = lb;
      o[i * 4 + 3] = a;
    }
  } else {
    // Coloured + filled — posterised flat colour with a drawn outline.
    const levels = 5;
    const step = 255 / (levels - 1);
    const q = (v: number) => Math.round(Math.round(v / step) * step);
    for (let i = 0; i < n; i++) {
      let r = q(d[i * 4]);
      let g = q(d[i * 4 + 1]);
      let b = q(d[i * 4 + 2]);
      const k = ink[i] * 0.82; // darken along the ink outline
      if (k > 0) {
        r *= 1 - k;
        g *= 1 - k;
        b *= 1 - k;
      }
      o[i * 4] = r;
      o[i * 4 + 1] = g;
      o[i * 4 + 2] = b;
      o[i * 4 + 3] = d[i * 4 + 3];
    }
  }

  ctx.putImageData(out, 0, 0);

  // Trim transparent margins so a downstream layout (e.g. text below the
  // design) sits flush against the real artwork, no empty gap.
  const b = opaqueBounds(o, w, h, 8);
  let finalCv: HTMLCanvasElement = cv;
  if (b && (b.w < w || b.h < h)) {
    const pad = Math.round(Math.max(w, h) * 0.015);
    const tx = Math.max(0, b.x - pad);
    const ty = Math.max(0, b.y - pad);
    const tw = Math.min(w - tx, b.w + pad * 2);
    const th = Math.min(h - ty, b.h + pad * 2);
    const tc = document.createElement("canvas");
    tc.width = tw;
    tc.height = th;
    const tctx = tc.getContext("2d");
    if (tctx) {
      tctx.drawImage(cv, tx, ty, tw, th, 0, 0, tw, th);
      finalCv = tc;
    }
  }

  return new Promise<Blob | null>((resolve) =>
    finalCv.toBlob((blob) => resolve(blob), "image/png"),
  );
}

// Crop the transparent margins off a PNG blob so its rectangle is the
// visible content. Used after background removal so anything laid out
// against the artwork (e.g. text below it) has no transparent gap.
export async function trimTransparent(blob: Blob): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("load failed"));
      im.src = url;
    });
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return blob;
    const cv = document.createElement("canvas");
    cv.width = w;
    cv.height = h;
    const ctx = cv.getContext("2d");
    if (!ctx) return blob;
    ctx.drawImage(img, 0, 0);
    let id: ImageData;
    try {
      id = ctx.getImageData(0, 0, w, h);
    } catch {
      return blob;
    }
    const b = opaqueBounds(id.data, w, h, 8);
    if (!b) return blob; // fully transparent — leave as-is
    const pad = Math.round(Math.max(w, h) * 0.015);
    const tx = Math.max(0, b.x - pad);
    const ty = Math.max(0, b.y - pad);
    const tw = Math.min(w - tx, b.w + pad * 2);
    const th = Math.min(h - ty, b.h + pad * 2);
    if (tw >= w && th >= h) return blob; // already tight
    const out = document.createElement("canvas");
    out.width = tw;
    out.height = th;
    const octx = out.getContext("2d");
    if (!octx) return blob;
    octx.drawImage(cv, tx, ty, tw, th, 0, 0, tw, th);
    return await new Promise<Blob>((resolve) =>
      out.toBlob((bb) => resolve(bb ?? blob), "image/png"),
    );
  } catch {
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}
